import { productCostBasisTerm } from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import {
  getProductCostBusinessMetricDetail,
  productCostBusinessMetricDetailQueryKey,
} from "./businessMetricDictionary";

export type ProductCostBusinessMetricDetailRoute =
  | { kind: "NONE" }
  | { kind: "INVALID" }
  | { kind: "DETAIL"; businessDate: string };

type ProductCostDetailLoader = typeof getProductCostBusinessMetricDetail;

type ProductCostBusinessMetricDetailPanelProps = {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  route: Exclude<ProductCostBusinessMetricDetailRoute, { kind: "NONE" }>;
  loader?: ProductCostDetailLoader;
};

function isCanonicalBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

export function productCostBusinessMetricDetailRouteFromSearch(
  search: string,
): ProductCostBusinessMetricDetailRoute {
  const params = new URLSearchParams(search);
  const views = params.getAll("view");
  if (views.length === 0) return { kind: "NONE" };

  const businessDates = params.getAll("businessDate");
  if (
    [...params.entries()].length !== 2 ||
    views.length !== 1 ||
    views[0] !== "PRODUCT_COST_DETAILS" ||
    businessDates.length !== 1
  ) {
    return { kind: "INVALID" };
  }
  const businessDate = businessDates[0] ?? "";
  return isCanonicalBusinessDate(businessDate)
    ? { kind: "DETAIL", businessDate }
    : { kind: "INVALID" };
}

function formatMoney(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function formatTime(timestamp: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone,
  }).format(new Date(timestamp));
}

function AuthorizedProductCostBusinessMetricDetail({
  operator,
  route,
  loader,
}: {
  operator: OperatorIdentity;
  route: Extract<ProductCostBusinessMetricDetailRoute, { kind: "DETAIL" }>;
  loader: ProductCostDetailLoader;
}): React.JSX.Element {
  const context = {
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    operatorId: operator.id,
  };
  const detail = useQuery({
    queryKey: productCostBusinessMetricDetailQueryKey(context, route.businessDate),
    queryFn: ({ signal }) => loader(route.businessDate, context, signal),
    refetchOnMount: "always",
    retry: false,
  });
  const responseMatchesSelection =
    detail.data?.tenantId === operator.tenantId &&
    detail.data.storeId === operator.storeId &&
    detail.data.businessDate === route.businessDate;
  const visibleDetail =
    !detail.isFetching && !detail.isError && responseMatchesSelection ? detail.data : undefined;
  const responseUnsafe =
    !detail.isFetching && detail.data !== undefined && !responseMatchesSelection;

  return (
    <main className="commercial-desk" aria-label="营业日商品成本完整明细">
      <header className="commerce-header">
        <div>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
          <small>{route.businessDate} · 当前实际出库营业日</small>
          <h1>营业日商品成本明细</h1>
          <p>
            本页只核对当前营业日实际售出后仍未退回的商品成本。成本在销售时冻结，后续采购价不会改写旧销售；退货按权威剩余价值扣回，未知成本保留为待补充，绝不按
            0 元计入。
          </p>
        </div>
      </header>

      {detail.isFetching ? (
        <output className="empty-state" aria-live="polite">
          正在核对 {route.businessDate}{" "}
          的完整商品成本账本，确认前不会显示旧日期、旧账号或旧门店的金额。
        </output>
      ) : detail.isError || responseUnsafe ? (
        <div className="error-band" role="alert">
          <span>
            这项商品成本明细暂时无法安全读取，旧明细和金额已隐藏；只影响本项查看，不表示整套 ERP
            离线。
          </span>
          <button
            className="button secondary compact"
            type="button"
            onClick={() => detail.refetch()}
          >
            重新读取本项成本
          </button>
        </div>
      ) : visibleDetail ? (
        <>
          <section className="summary-grid" aria-label="营业日商品成本汇总">
            <div>
              <span>已知成本合计</span>
              <strong>{formatMoney(visibleDetail.valueFen)}</strong>
            </div>
            <div>
              <span>已核对成本行数</span>
              <strong>{visibleDetail.factCount} 行</strong>
            </div>
            <div>
              <span>成本待补充行数</span>
              <strong>{visibleDetail.unknownFactCount} 行</strong>
            </div>
          </section>

          {visibleDetail.items.length === 0 ? (
            <section className="empty-state" aria-label="营业日商品成本为空">
              <strong>该营业日没有商品成本明细</strong>
              <p>
                当前营业日完整出库账本已核对；这里只表示没有剩余销售商品成本，不代表订单或营业实收为零。
              </p>
            </section>
          ) : (
            <section aria-label="营业日逐笔商品成本">
              <div className="commerce-section-title">
                <div>
                  <h2>逐笔剩余商品成本</h2>
                  <p>共 {visibleDetail.itemCount} 行；已知成本进入合计，待补充成本保持未知。</p>
                </div>
              </div>
              <div className="member-lifecycle-timeline-list">
                {visibleDetail.items.map((item) => {
                  const basis = productCostBasisTerm(item.costBasis);
                  return (
                    <article key={item.costRecordNumber}>
                      <div>
                        <strong>成本明细号 {item.costRecordNumber}</strong>
                        <time dateTime={item.occurredAt}>
                          {formatTime(item.occurredAt, visibleDetail.timeZone)}
                        </time>
                      </div>
                      <dl>
                        <div>
                          <dt>原订单</dt>
                          <dd>
                            <Link
                              className="member-lifecycle-order-link"
                              to={`/orders?orderQuery=${encodeURIComponent(item.orderNumber)}`}
                            >
                              {item.orderNumber}
                            </Link>
                          </dd>
                        </div>
                        <div>
                          <dt>原订单营业日</dt>
                          <dd>{item.orderBusinessDate}</dd>
                        </div>
                        <div>
                          <dt>实际销售营业日 / 时间</dt>
                          <dd>
                            {item.saleBusinessDate} /{` `}
                            <time dateTime={item.occurredAt}>
                              {formatTime(item.occurredAt, visibleDetail.timeZone)}
                            </time>
                          </dd>
                        </div>
                        <div>
                          <dt>桌号</dt>
                          <dd>{item.tableNumber}</dd>
                        </div>
                        <div>
                          <dt>商品</dt>
                          <dd>
                            {item.productCode} · {item.productName}
                          </dd>
                        </div>
                        <div>
                          <dt>售出 / 已退 / 剩余</dt>
                          <dd>
                            {item.soldQuantity} / {item.returnedQuantity} / {item.remainingQuantity}
                          </dd>
                        </div>
                        <div>
                          <dt>成本依据</dt>
                          <dd>
                            <strong>{basis.label}</strong>
                            <p>{basis.explanation}</p>
                          </dd>
                        </div>
                        <div>
                          <dt>剩余成本</dt>
                          <dd>
                            {item.remainingCostFen === null
                              ? "成本待补充（不计 0 元）"
                              : formatMoney(item.remainingCostFen)}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      ) : null}
    </main>
  );
}

export function ProductCostBusinessMetricDetailPanel({
  operator,
  effectiveRoles,
  route,
  loader = getProductCostBusinessMetricDetail,
}: ProductCostBusinessMetricDetailPanelProps): React.JSX.Element {
  if (route.kind === "INVALID") {
    return (
      <main className="commercial-desk" aria-label="商品成本明细链接无效">
        <div className="error-band" role="alert">
          <span>商品成本明细的营业日期无法安全确认，系统没有读取或展示普通订单列表。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  const authorized = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  if (!authorized) {
    return (
      <main className="commercial-desk" aria-label="商品成本明细无查看权限">
        <div className="error-band" role="alert">
          <span>当前账号不能查看全店商品成本明细，系统没有读取普通订单或成本金额。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <AuthorizedProductCostBusinessMetricDetail
      key={`${operator.tenantId}\u0000${operator.storeId}\u0000${operator.id}\u0000${route.businessDate}`}
      operator={operator}
      route={route}
      loader={loader}
    />
  );
}
